import React from 'react';
import { Helmet } from 'react-helmet-async';

// Canonical origin — must match Netlify's primary domain (www redirects here).
export const SITE_URL = 'https://indianstockmarketgpt.com';

const DEFAULT_IMAGE = `${SITE_URL}/web-app-manifest-512x512.png`;

interface SeoMetaProps {
  title: string;
  description: string;
  keywords?: string;
  url?: string;
  image?: string;
  type?: string;
  siteName?: string;
  twitterHandle?: string;
  robots?: string;
  children?: React.ReactNode;
}

/**
 * Per-page <head> metadata. Rendered through react-helmet-async so the tags
 * land in <head> (React 18 does not hoist <title>/<meta>/<link> on its own).
 * `children` (e.g. JSON-LD <script> blocks) render in place — Google reads
 * structured data anywhere in the document.
 */
const SeoMeta: React.FC<SeoMetaProps> = ({
  title,
  description,
  keywords,
  url,
  image,
  type = 'website',
  siteName = 'IndianStockMarketGPT',
  twitterHandle,
  robots,
  children,
}) => {
  const ogImage = image || DEFAULT_IMAGE;
  return (
    <>
      {/* defer={false}: apply head changes synchronously instead of in
          requestAnimationFrame, which headless crawlers/background tabs throttle. */}
      <Helmet defer={false}>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        {robots && <meta name="robots" content={robots} />}

        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {url && <meta property="og:url" content={url} />}
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content={type} />
        <meta property="og:site_name" content={siteName} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}

        {/* Canonical */}
        {url && <link rel="canonical" href={url} />}
      </Helmet>
      {children}
    </>
  );
};

export default SeoMeta;
